import { CarrierLogo } from "@/components/carriers/CarrierLogo";
import { DataTable } from "@/components/table/DataTable";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { apiClient } from "@/lib/api/client";
import type { ShipmentDetail, ShipmentListItem, ShipmentStatus } from "@/lib/api/types";
import { formatCurrency, formatDate, formatStatus, statusClass } from "@/lib/tms/format";
import * as React from "react";

function buildCsv(rows: ShipmentListItem[]) {
  const header = [
    "id",
    "external_id",
    "created_date",
    "status",
    "mode",
    "carrier",
    "tracking_number",
    "destination",
    "total_cost",
  ];

  const lines = rows.map((row) =>
    [
      row.id,
      row.externalId ?? "",
      row.createdDate ?? "",
      row.status,
      row.mode,
      row.carrier.name,
      row.trackingNumber ?? "",
      `${row.destination.city ?? ""}, ${row.destination.state ?? ""}`,
      row.totalCost,
    ]
      .map((field) => `"${String(field).replaceAll('"', '""')}"`)
      .join(","),
  );

  return [header.join(","), ...lines].join("\n");
}

export default function ShipmentsPage() {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [shipments, setShipments] = React.useState<ShipmentListItem[]>([]);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [selectedDetail, setSelectedDetail] = React.useState<ShipmentDetail | null>(null);

  const loadShipments = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.getShipments({ page: 1, pageSize: 100 });
      setShipments(response.items);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Unable to load shipments.");
      setShipments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadShipments();
  }, [loadShipments]);

  const loadDetail = async (id: string) => {
    setSelectedId(id);
    setSelectedDetail(null);
    try {
      const detail = await apiClient.getShipmentById(id);
      setSelectedDetail(detail);
    } catch {
      setSelectedDetail(null);
    }
  };

  const [downloadingLabel, setDownloadingLabel] = React.useState(false);
  const [downloadingBol, setDownloadingBol] = React.useState(false);

  const downloadPdf = async (type: "label" | "bol") => {
    if (!selectedId) return;
    const setter = type === "label" ? setDownloadingLabel : setDownloadingBol;
    setter(true);
    try {
      const blob = type === "label"
        ? await apiClient.getShipmentLabelPdf(selectedId)
        : await apiClient.getShipmentBolPdf(selectedId);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${type}-${selectedId}.pdf`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (dlError) {
      setError(dlError instanceof Error ? dlError.message : `Unable to download ${type}.`);
    } finally {
      setter(false);
    }
  };

  const exportCsv = () => {
    const blob = new Blob([buildCsv(shipments)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `shipments-${new Date().toISOString().split("T")[0]}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>All Shipments</CardTitle>
          <CardDescription>Live shipment data derived from bookings and carrier activity.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error ? <p className="text-sm text-destructive">{error}</p> : null}

          {loading ? (
            <div className="space-y-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-10 w-24" />
                  <Skeleton className="h-6 w-20 rounded-full" />
                  <Skeleton className="h-6 w-12" />
                  <Skeleton className="h-6 flex-1" />
                  <Skeleton className="h-6 w-28" />
                  <Skeleton className="h-6 w-16" />
                  <Skeleton className="h-6 w-20" />
                  <Skeleton className="h-8 w-14" />
                </div>
              ))}
            </div>
          ) : null}

          <DataTable
            rows={shipments}
            getRowId={(row) => row.id}
            onExportCsv={exportCsv}
            columns={[
              {
                key: "id",
                label: "Shipment",
                sortable: true,
                render: (row) => (
                  <div>
                    <div className="font-medium">#{row.id}</div>
                    <div className="text-xs text-muted-foreground">{row.externalId ?? "No external id"}</div>
                  </div>
                ),
                getSortValue: (row) => row.id,
              },
              {
                key: "status",
                label: "Status",
                sortable: true,
                render: (row) => (
                  <span className={`rounded-full px-2 py-0.5 text-xs ${statusClass(row.status)}`}>
                    {formatStatus(row.status)}
                  </span>
                ),
                getSortValue: (row) => row.status,
                filter: {
                  type: "select",
                  options: [
                    { label: "Pending Payment", value: "pending_payment" },
                    { label: "Ready To Prepare", value: "ready_to_prepare" },
                    { label: "Ready For Pickup", value: "ready_for_pickup" },
                    { label: "In Transit", value: "in_transit" },
                    { label: "Delivered", value: "delivered" },
                    { label: "On Hold", value: "on_hold" },
                    { label: "Cancelled", value: "cancelled" },
                  ],
                },
              },
              {
                key: "mode",
                label: "Mode",
                sortable: true,
                render: (row) => row.mode.toUpperCase(),
                getSortValue: (row) => row.mode,
                filter: {
                  type: "select",
                  options: [
                    { label: "LTL", value: "ltl" },
                    { label: "Parcel", value: "parcel" },
                  ],
                },
              },
              {
                key: "carrier",
                label: "Carrier",
                sortable: true,
                render: (row) => (
                  <div className="flex items-center gap-2">
                    <CarrierLogo code={row.carrier.code} name={row.carrier.name} className="size-8 rounded-md" />
                    <div className="min-w-0">
                      <p className="truncate font-medium">{row.carrier.name}</p>
                      <p className="text-xs text-muted-foreground">{row.carrier.code || "N/A"}</p>
                    </div>
                  </div>
                ),
                getSortValue: (row) => row.carrier.name,
                filter: { type: "select" },
              },
              {
                key: "destination",
                label: "Destination",
                sortable: true,
                render: (row) => `${row.destination.city ?? "-"}, ${row.destination.state ?? "-"}`,
                getSortValue: (row) => `${row.destination.city ?? ""} ${row.destination.state ?? ""}`,
                filter: {
                  type: "text",
                  placeholder: "Destination",
                },
              },
              {
                key: "cost",
                label: "Cost",
                sortable: true,
                render: (row) => formatCurrency(row.totalCost),
                getSortValue: (row) => row.totalCost,
              },
              {
                key: "created",
                label: "Created",
                sortable: true,
                render: (row) => formatDate(row.createdDate),
                getSortValue: (row) => row.createdDate ?? "",
              },
              {
                key: "action",
                label: "Action",
                render: (row) => (
                  <Button size="sm" variant="outline" onClick={() => void loadDetail(row.id)}>
                    View
                  </Button>
                ),
              },
            ]}
          />
        </CardContent>
      </Card>

      {selectedId ? (
        <Card>
          <CardHeader>
            <CardTitle>Shipment Detail #{selectedId}</CardTitle>
            <CardDescription>{selectedDetail ? "Live detail from retrieve service" : "Loading details..."}</CardDescription>
          </CardHeader>
          <CardContent>
            {selectedDetail ? (
              <div className="space-y-3">
                <div className="grid grid-cols-1 gap-2 md:grid-cols-3 text-sm">
                  <div>
                    <p className="text-muted-foreground">Tracking</p>
                    <p className="font-medium">{selectedDetail.trackingNumber ?? "-"}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Service</p>
                    <p className="font-medium">{selectedDetail.serviceType ?? "-"}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Status</p>
                    <p className="font-medium">{formatStatus(selectedDetail.status as ShipmentStatus)}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={downloadingLabel}
                    onClick={() => void downloadPdf("label")}
                  >
                    {downloadingLabel ? "Generating..." : "Download Label"}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={downloadingBol}
                    onClick={() => void downloadPdf("bol")}
                  >
                    {downloadingBol ? "Generating..." : "Download BOL"}
                  </Button>
                </div>
                <div>
                  <p className="text-sm font-medium mb-1">Tracking Events</p>
                  <ul className="space-y-1 text-sm">
                    {selectedDetail.trackingEvents.length === 0 ? (
                      <li className="text-muted-foreground">No tracking events recorded.</li>
                    ) : (
                      selectedDetail.trackingEvents.map((event) => (
                        <li key={event.id} className="rounded-lg border-[0.5px] border-border px-3 py-2">
                          <span className="font-medium">{event.status ?? "Unknown"}</span> - {event.description ?? "-"} ({formatDate(event.timestamp)})
                        </li>
                      ))
                    )}
                  </ul>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </div>
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            )}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
